import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";

import type {
  LabeledNodePreviewType,
  NodePreviewLabel,
  NodePreviewType,
} from "@/components/expression/preview/types";
import type { NodeModel } from "@/models/nodes/types";

export type PreviewNode<T extends NodePreviewType = NodePreviewType> =
  | LabeledNodePreviewType<T>
  | NodeModel<T>;

export function convertNodeToPreview<T extends NodeModel["type"]>(
  node: T extends NodePreviewType ? PreviewNode<T> : NodeModel<T>,
): LabeledNodePreviewType | null {
  if (!(node instanceof BaseNodeModel)) {
    return node;
  }

  switch (node.type) {
    case "number":
      return {
        type: "number",
        value: node.value,
        labels: getNodeLabels(node),
      };
    case "variable":
      return {
        type: "variable",
        value: node.value,
        color: node.color,
        labels: getNodeLabels(node),
      };

    case "constant":
      return {
        type: "constant",
        value: node.value,
        color: node.color,
        labels: getNodeLabels(node),
      };

    case "function":
      return {
        type: "function",
        value: node.value,
        color: node.color,
        functionView: node.functionView,
        labels: getNodeLabels(node),
      };

    case "operatorSymbol":
      return {
        type: "operatorSymbol",
        value: node.value,
        labels: getNodeLabels(node),
      };

    case "operator":
      return {
        type: "operator",
        operatorType: node.operatorType,
        value: node.operatorSymbol,
        labels: getNodeLabels(node),
      };

    case "plainText":
      return {
        type: "plainText",
        value: node.value,
        labels: getNodeLabels(node),
      };

    case "group":
      return {
        type: "group",
        openingSymbol: node.openingSymbol,
        closingSymbol: node.closingSymbol,
        labels: getNodeLabels(node),
        children:
          (node.inlineNode?.nodes
            .map((n) => convertNodeToPreview(n))
            .filter(Boolean) as LabeledNodePreviewType[]) ?? [],
      };

    default:
      return null;
  }
}

export function getNodeLabels<T extends NodeModel["type"]>(node: NodeModel<T>): NodePreviewLabel[] {
  if (!("symbolNode" in node) || !node.symbolNode) return [];

  const { labelNodes = [] } = node.symbolNode;

  return labelNodes.map((att) => {
    const nodes = att.inlineNode?.nodes.map(convertNodeToPreview).filter(Boolean) ?? [];
    return {
      position: att.position,
      nodes,
    };
  });
}
